import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { NavBar } from "../navbar/navbar";
import { useLanguage } from "../../i18n/LanguageProvider";
import { useCurrency } from "../../currency/CurrencyProvider";
import {
  DailyThreadData,
  ForumCategory,
  ForumError,
  ForumMe,
  ForumPost,
  ForumStats,
  ForumThreadDetail,
  ForumThreadSummary,
  ThreadSort,
  useForumApi,
} from "./forumApi";
import { DailyCard, dayLabel } from "./DailyCard";
import { ForumNameDialog } from "./ForumNameDialog";
import { Markdown, stripMarkdown } from "./Markdown";
import { Composer, ComposerHandle } from "./Composer";
import { Avatar, AvatarStack, CategoryChip, Icon, fullDate, timeAgo } from "./forumUi";
import { track } from "../../utils/track";
import "./forum.css";

const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <>
    <NavBar />
    <div className="fm-page">{children}</div>
  </>
);

const Loading = () => (
  <div className="fm-loading">
    <Spinner animation="border" size="sm" />
  </div>
);

const errorText = (e: unknown) => (e instanceof Error ? e.message : String(e));

// The caller's forum identity, plus the name dialog that writing requires.
const useForumMe = () => {
  const forum = useForumApi();
  const [me, setMe] = useState<ForumMe | null>(null);
  const [nameOpen, setNameOpen] = useState(false);
  useEffect(() => {
    forum.me().then(setMe).catch(() => setMe({ forumName: null, nameLocked: false, banned: false, canModerate: false }));
  }, [forum]);
  const dialog = (
    <ForumNameDialog
      show={nameOpen}
      current={me?.forumName ?? null}
      onClose={() => setNameOpen(false)}
      onSaved={(forumName) => {
        setMe((m) => (m ? { ...m, forumName } : m));
        setNameOpen(false);
      }}
    />
  );
  return { me, openName: () => setNameOpen(true), dialog };
};

const useCategories = () => {
  const forum = useForumApi();
  const { language } = useLanguage();
  const [categories, setCategories] = useState<ForumCategory[] | null>(null);
  useEffect(() => {
    forum.categories().then((c) => setCategories(c.categories)).catch(() => setCategories([]));
  }, [forum]);
  const byslug = useCallback((slug?: string | null) => categories?.find((c) => c.slug === slug), [categories]);
  const name = useCallback(
    (c?: ForumCategory) => (c ? (language === "sv" ? c.nameSv : c.nameEn) : ""),
    [language],
  );
  const desc = useCallback(
    (c?: ForumCategory) => (c ? (language === "sv" ? c.descriptionSv : c.descriptionEn) ?? "" : ""),
    [language],
  );
  return { categories, byslug, name, desc };
};

// Small popover menu (post "…" menu, moderation menu).
const Menu: React.FC<{ label: React.ReactNode; title: string; children: React.ReactNode; align?: "left" | "right" }> = ({
  label,
  title,
  children,
  align = "right",
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);
  return (
    <div className="fm-menu-wrap" ref={ref}>
      <button type="button" className="fm-iconbtn" title={title} aria-label={title} onClick={() => setOpen((o) => !o)}>
        {label}
      </button>
      {open && (
        <div className={`fm-menu ${align === "left" ? "is-left" : ""}`} onClick={() => setOpen(false)}>
          {children}
        </div>
      )}
    </div>
  );
};

const MenuItem: React.FC<{ icon: string; onClick: () => void; danger?: boolean; children: React.ReactNode }> = ({
  icon,
  onClick,
  danger,
  children,
}) => (
  <button type="button" className={`fm-menu-item ${danger ? "is-danger" : ""}`} onClick={onClick}>
    <Icon name={icon} size={15} />
    {children}
  </button>
);

const copyLink = (path: string) => {
  const url = `${window.location.origin}${path}`;
  navigator.clipboard?.writeText(url).catch(() => undefined);
  track("feature_use", "forum_share");
};

// ---------------------------------------------------------------------------
// Forum home
// ---------------------------------------------------------------------------

// The daily thread in the hero: the forecast for the days after tomorrow (one
// column per day, cheapest day highlighted) and tomorrow's known price.
const DailyHero: React.FC<{ thread: ForumThreadSummary & { data: DailyThreadData | null } }> = ({ thread }) => {
  const { t, locale } = useLanguage();
  const { format } = useCurrency();
  const d = thread.data;
  const zone = d?.areas.includes("SE3") ? "SE3" : d?.areas[0];
  const outlook = (d?.outlook ?? []).filter((o) => zone && o.values[zone] !== undefined);
  const vals = outlook.map((o) => o.values[zone!]);
  const max = Math.max(0.001, ...vals);
  const min = Math.min(...vals);
  const cheapest = outlook.find((o) => o.values[zone!] === min);
  const tomorrow = zone ? d?.day[zone] : undefined;
  const short = (date: string) => dayLabel(date, locale, "short", "short");

  return (
    <Link to={`/forum/t/${thread.id}`} className="fm-hero-daily">
      <div className="fm-hero-daily__top">
        <span className="fm-live-dot" />
        <span>{t("forum.daily")}</span>
        {outlook.length > 0 && (
          <span className="fm-hero-daily__date">
            {zone} · {short(outlook[0].date)} – {short(outlook[outlook.length - 1].date)}
          </span>
        )}
      </div>
      {outlook.length > 0 ? (
        <>
          <div className="fm-hero-daily__price">
            <span className="fm-hero-daily__kicker">{t("forum.daily.cheapestDay")}</span>
            <span className="fm-hero-daily__value">
              {cheapest ? dayLabel(cheapest.date, locale, "long", "short") : "–"}
            </span>
            <span className="fm-hero-daily__unit">
              {t("forum.daily.forecastMean", { value: format(min) })}
            </span>
          </div>
          <div className="fm-hero-week">
            {outlook.map((o) => {
              const v = o.values[zone!];
              return (
                <div key={o.date} className={`fm-hero-week__day ${o === cheapest ? "is-cheapest" : ""}`}>
                  <span className="fm-hero-week__bar">
                    <span style={{ height: `${Math.max((v / max) * 100, 6)}%` }} />
                  </span>
                  <span className="fm-hero-week__value">{format(v).replace(/\s?(kr|c|NOK|DKK)$/, "")}</span>
                  <span className="fm-hero-week__name">{short(o.date).split(" ")[0]}</span>
                </div>
              );
            })}
          </div>
          {tomorrow && d?.pricesFinal && (
            <div className="fm-hero-daily__facts">
              <span>
                {t("forum.daily.tomorrowKnown")} <b>{format(tomorrow.avg)}</b>
              </span>
              {tomorrow.forecastAvg !== null && (
                <span>
                  {t("forum.daily.weSaid")} <b>{format(tomorrow.forecastAvg)}</b>
                </span>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="fm-hero-daily__title">{thread.title}</div>
      )}
      <div className="fm-hero-daily__foot">
        <AvatarStack names={thread.participants} size={22} />
        <span>{thread.replyCount === 1 ? t("forum.reply1") : t("forum.replies", { n: thread.replyCount })}</span>
        <span>
          <Icon name="eye" size={14} /> {thread.viewCount.toLocaleString(locale)}
        </span>
        <span className="fm-hero-daily__cta">
          {t("forum.joinDiscussion")} <span aria-hidden>→</span>
        </span>
      </div>
    </Link>
  );
};

const ThreadRow: React.FC<{
  th: ForumThreadSummary;
  cat?: ForumCategory;
  catName: string;
  showCategory: boolean;
  onCategory?: () => void;
}> = ({ th, cat, catName, showCategory, onCategory }) => {
  const { t, locale } = useLanguage();
  const excerpt = th.excerpt ? stripMarkdown(th.excerpt) : null;
  return (
    <li className={`fm-row ${th.isPinned ? "is-pinned" : ""} ${th.kind === "daily" ? "is-daily" : ""}`}>
      <div className="fm-row__score" title={t("forum.votes")}>
        <Icon name="up" size={14} filled />
        <span>{th.score}</span>
      </div>
      <Avatar name={th.authorName} system={th.kind === "daily"} size={40} />
      <div className="fm-row__main">
        <Link to={`/forum/t/${th.id}`} className="fm-row__title">
          {th.isPinned && <Icon name="pin" size={14} className="fm-row__flag" />}
          {th.isLocked && <Icon name="lock" size={14} className="fm-row__flag" />}
          {th.isHidden && <span className="fm-badge danger">{t("forum.hiddenThread")}</span>}
          {th.title}
        </Link>
        {excerpt && <p className="fm-row__excerpt">{excerpt}</p>}
        <div className="fm-row__meta">
          {showCategory && <CategoryChip name={catName} color={cat?.color} icon={cat?.icon} onClick={onCategory} />}
          <span className="fm-row__author">{th.authorName ?? "–"}</span>
          <span className="fm-dotsep" />
          <span title={fullDate(th.createdAt, locale)}>{timeAgo(th.createdAt, locale)}</span>
        </div>
      </div>
      <div className="fm-row__side">
        <AvatarStack names={th.participants} size={24} />
        <div className="fm-row__stats">
          <span className="fm-row__replies">
            <Icon name="comment" size={14} /> {th.replyCount}
          </span>
          <span className="fm-row__views" title={t("forum.viewsLabel")}>
            <Icon name="eye" size={14} /> {th.viewCount.toLocaleString(locale)}
          </span>
          <span className="fm-row__active" title={fullDate(th.lastPostAt, locale)}>
            {timeAgo(th.lastPostAt, locale)}
          </span>
        </div>
      </div>
    </li>
  );
};

// Moderators: shortcut to the overview and the daily-thread trigger.
const ModPanel = () => {
  const { t } = useLanguage();
  const forum = useForumApi();
  const [note, setNote] = useState<string | null>(null);
  const daily = async (rebuild: boolean) => {
    setNote(null);
    try {
      const r = await forum.modDaily(rebuild);
      setNote(t("forum.mod.dailyResult", { status: t(`forum.mod.status.${r.status}`), date: r.date }));
    } catch (e) {
      setNote(errorText(e));
    }
  };
  return (
    <div className="fm-modpanel">
      <Link to="/admin?tab=forum" className="fm-mod-link">
        <Icon name="shield" size={15} /> {t("forum.modPanel")}
      </Link>
      <div className="fm-modpanel__row">
        <button type="button" className="fm-btn fm-btn--ghost fm-btn--sm" onClick={() => daily(false)}>
          {t("forum.mod.createDaily")}
        </button>
        <button type="button" className="fm-btn fm-btn--ghost fm-btn--sm" onClick={() => daily(true)}>
          {t("forum.mod.rebuildDaily")}
        </button>
      </div>
      {note && <div className="fm-note">{note}</div>}
    </div>
  );
};

// /forum and /forum/c/:slug
export const ForumHome = () => {
  const { slug } = useParams();
  const { t } = useLanguage();
  const forum = useForumApi();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { categories, byslug, name, desc } = useCategories();
  const { me, openName, dialog } = useForumMe();
  const [stats, setStats] = useState<ForumStats | null>(null);
  const [daily, setDaily] = useState<(ForumThreadSummary & { data: DailyThreadData | null }) | null>(null);
  const [threads, setThreads] = useState<ForumThreadSummary[] | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const sort = (params.get("sort") as ThreadSort) || "active";
  const q = params.get("q") ?? "";
  const [query, setQuery] = useState(q);
  const category = slug ?? null;
  const cat = byslug(category);

  useEffect(() => {
    forum.stats().then(setStats).catch(() => undefined);
    forum.latestDaily().then((d) => setDaily(d.thread)).catch(() => undefined);
  }, [forum]);

  useEffect(() => {
    setThreads(null);
    setPage(0);
    forum
      .threads({ category, sort, q })
      .then((r) => {
        setThreads(r.threads);
        setHasMore(r.hasMore);
      })
      .catch((e) => setError(errorText(e)));
  }, [forum, category, sort, q]);

  const loadMore = async () => {
    const r = await forum.threads({ category, sort, q, page: page + 1 });
    setThreads((prev) => [...(prev ?? []), ...r.threads]);
    setHasMore(r.hasMore);
    setPage((p) => p + 1);
  };

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const goCategory = (s: string | null) => navigate(`${s ? `/forum/c/${s}` : "/forum"}${params.toString() ? `?${params}` : ""}`);

  return (
    <Shell>
      <section className="fm-hero">
        <div className="fm-hero__glow" />
        <div className="fm-hero__grid" />
        <div className="fm-hero__inner">
          <div className="fm-hero__text">
            <span className="fm-eyebrow">{t("forum.eyebrow")}</span>
            <h1 className="fm-hero__title">{cat ? name(cat) : t("forum.title")}</h1>
            <p className="fm-hero__lead">{cat ? desc(cat) : t("forum.lead")}</p>
            {stats && (
              <div className="fm-hero__stats">
                <span>
                  <b>{stats.threads}</b> {t("forum.statThreads")}
                </span>
                <span>
                  <b>{stats.posts}</b> {t("forum.statPosts")}
                </span>
                <span>
                  <b>{stats.members}</b> {t("forum.statMembers")}
                </span>
                <span>
                  <b>{stats.views.toLocaleString()}</b> {t("forum.statViews")}
                </span>
                {stats.postsToday > 0 && (
                  <span className="is-live">
                    <span className="fm-live-dot" />
                    <b>{stats.postsToday}</b> {t("forum.statToday")}
                  </span>
                )}
              </div>
            )}
            <form
              className="fm-search"
              onSubmit={(e) => {
                e.preventDefault();
                setParam("q", query.trim() || null);
              }}
            >
              <Icon name="search" size={18} />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("forum.searchPlaceholder")}
                aria-label={t("forum.searchPlaceholder")}
              />
              {q && (
                <button
                  type="button"
                  className="fm-search__clear"
                  onClick={() => {
                    setQuery("");
                    setParam("q", null);
                  }}
                >
                  ×
                </button>
              )}
              <Link to={`/forum/ny${category ? `?category=${category}` : ""}`} className="fm-btn fm-btn--primary">
                <Icon name="plus" size={16} /> {t("forum.newThread")}
              </Link>
            </form>
          </div>
          {daily && <DailyHero thread={daily} />}
        </div>
      </section>

      {categories && categories.length > 0 && (
        <nav className="fm-cats-row" aria-label={t("forum.categories")}>
          <button type="button" className={`fm-cat-tile ${!category ? "is-active" : ""}`} onClick={() => goCategory(null)}>
            <span className="fm-cat-tile__icon is-all">
              <Icon name="flame" size={18} />
            </span>
            <span className="fm-cat-tile__name">{t("forum.allThreads")}</span>
            <span className="fm-cat-tile__count">{stats?.threads ?? ""}</span>
          </button>
          {categories.map((c) => (
            <button
              key={c.slug}
              type="button"
              className={`fm-cat-tile ${category === c.slug ? "is-active" : ""}`}
              style={{ "--cat": c.color ?? "#64748b" } as React.CSSProperties}
              onClick={() => goCategory(c.slug)}
            >
              <span className="fm-cat-tile__icon">
                <Icon name={c.icon ?? "chat"} size={18} />
              </span>
              <span className="fm-cat-tile__name">{name(c)}</span>
              <span className="fm-cat-tile__count">{c.threadCount}</span>
            </button>
          ))}
        </nav>
      )}

      <div className="fm-layout">
        <main className="fm-main">
          <div className="fm-toolbar">
            <div className="fm-seg" role="tablist">
              {(["active", "new", "top"] as ThreadSort[]).map((s) => (
                <button
                  key={s}
                  type="button"
                  role="tab"
                  aria-selected={sort === s}
                  className={sort === s ? "is-active" : ""}
                  onClick={() => setParam("sort", s === "active" ? null : s)}
                >
                  <Icon name={s === "active" ? "flame" : s === "new" ? "clock" : "trophy"} size={14} />
                  {t(`forum.sort.${s}`)}
                </button>
              ))}
            </div>
            {q && (
              <span className="fm-toolbar__note">
                {t("forum.searchResults", { q })}
              </span>
            )}
          </div>

          {error && <div className="alert alert-danger">{t("forum.loadError")}</div>}
          {!threads && !error && <Loading />}
          {threads && threads.length === 0 && (
            <div className="fm-empty">
              <Icon name="chat" size={28} />
              <p>{q ? t("forum.noResults") : t("forum.noThreads")}</p>
              <Link to={`/forum/ny${category ? `?category=${category}` : ""}`} className="fm-btn fm-btn--primary">
                {t("forum.newThread")}
              </Link>
            </div>
          )}
          {threads && threads.length > 0 && (
            <ul className="fm-rows">
              {threads.map((th) => (
                <ThreadRow
                  key={th.id}
                  th={th}
                  cat={byslug(th.category)}
                  catName={name(byslug(th.category))}
                  showCategory={!category}
                  onCategory={() => goCategory(th.category)}
                />
              ))}
            </ul>
          )}
          {hasMore && (
            <button type="button" className="fm-btn fm-btn--ghost fm-more" onClick={loadMore}>
              {t("forum.loadMore")}
            </button>
          )}
        </main>

        <aside className="fm-aside">
          <section className="fm-panel fm-panel--me">
            {me?.forumName ? (
              <>
                <div className="fm-me">
                  <Avatar name={me.forumName} size={44} />
                  <div>
                    <div className="fm-me__name">{me.forumName}</div>
                    {me.nameLocked ? (
                      <span className="fm-muted fm-small">{t("forum.memberSince")}</span>
                    ) : (
                      <button type="button" className="fm-linkbtn" onClick={openName}>
                        {t("forum.changeName")}
                      </button>
                    )}
                  </div>
                </div>
                {me.canModerate && <ModPanel />}
              </>
            ) : (
              <>
                <div className="fm-panel__title">{t("forum.joinTitle")}</div>
                <p className="fm-muted">{t("forum.joinLead")}</p>
                <button type="button" className="fm-btn fm-btn--primary fm-btn--block" onClick={openName}>
                  {t("forum.nameTitle")}
                </button>
              </>
            )}
          </section>

          {stats && stats.topMembers.length > 0 && (
            <section className="fm-panel">
              <div className="fm-panel__title">
                <Icon name="trophy" size={15} /> {t("forum.topMembers")}
              </div>
              <ol className="fm-leaders">
                {stats.topMembers.map((m, i) => (
                  <li key={m.name}>
                    <span className="fm-leaders__rank">{i + 1}</span>
                    <Avatar name={m.name} size={30} />
                    <span className="fm-leaders__name">{m.name}</span>
                    <span className="fm-leaders__stat" title={t("forum.votes")}>
                      <Icon name="up" size={12} filled /> {m.votes}
                    </span>
                  </li>
                ))}
              </ol>
            </section>
          )}

          <section className="fm-panel">
            <div className="fm-panel__title">
              <Icon name="shield" size={15} /> {t("forum.rulesTitle")}
            </div>
            <ul className="fm-rules">
              <li>{t("forum.rule1")}</li>
              <li>{t("forum.rule2")}</li>
              <li>{t("forum.rule3")}</li>
              <li>{t("forum.rule4")}</li>
            </ul>
          </section>
        </aside>
      </div>
      {dialog}
    </Shell>
  );
};

// ---------------------------------------------------------------------------
// Thread
// ---------------------------------------------------------------------------

const PostCard: React.FC<{
  post: ForumPost;
  index: number;
  canModerate: boolean;
  loggedIn: boolean;
  locked: boolean;
  highlight: boolean;
  onReply: (post: ForumPost) => void;
  onChanged: () => void;
  onHistory: (post: ForumPost) => void;
}> = ({ post, index, canModerate, loggedIn, locked, highlight, onReply, onChanged, onHistory }) => {
  const { t, locale } = useLanguage();
  const forum = useForumApi();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(post.body ?? "");
  const [note, setNote] = useState<string | null>(null);
  const [vote, setVote] = useState({ score: post.score, mine: post.myVote });

  useEffect(() => setVote({ score: post.score, mine: post.myVote }), [post.score, post.myVote]);

  const act = async (fn: () => Promise<unknown>) => {
    setNote(null);
    try {
      await fn();
      onChanged();
    } catch (e) {
      setNote(errorText(e));
    }
  };

  const toggleVote = async () => {
    if (!loggedIn || post.mine || post.isSystem || post.status !== "ok") return;
    const up = !vote.mine;
    setVote((v) => ({ score: v.score + (up ? 1 : -1), mine: up }));
    try {
      const r = await forum.vote(post.id, up);
      setVote({ score: r.score, mine: r.myVote });
      if (up) track("feature_use", "forum_vote");
    } catch (e) {
      setVote({ score: post.score, mine: post.myVote });
      setNote(errorText(e));
    }
  };

  const ok = post.status === "ok";

  const showReports = async () => {
    try {
      const r = await forum.modPostReports(post.id);
      setNote(
        r.reports
          .map((x) => `${x.reporter ?? "–"}: ${x.reason || t("forum.mod.noReason")}`)
          .join(" · ") || t("forum.mod.noReports"),
      );
    } catch (e) {
      setNote(errorText(e));
    }
  };

  const renameAuthor = () => {
    if (!post.authorId) return;
    const name = window.prompt(t("forum.mod.renamePrompt", { name: post.authorName ?? "" }), post.authorName ?? "");
    if (name && name.trim() && name.trim() !== post.authorName) act(() => forum.modRename(post.authorId!, name.trim()));
  };

  const toggleBan = () => {
    if (!post.authorId) return;
    const q = post.authorBanned
      ? t("forum.mod.unbanConfirm", { name: post.authorName ?? "" })
      : t("forum.mod.banConfirm", { name: post.authorName ?? "" });
    if (window.confirm(q)) act(() => forum.modBan(post.authorId!, !post.authorBanned));
  };

  return (
    <article
      className={`fm-post ${index === 0 ? "is-op" : ""} ${!ok ? "is-muted" : ""} ${highlight ? "is-highlight" : ""} ${
        post.isModerator ? "is-mod" : ""
      }`}
      id={`p-${post.id}`}
    >
      <div className="fm-post__gutter">
        <Avatar name={post.authorName} system={post.isSystem} size={index === 0 ? 46 : 40} />
      </div>
      <div className="fm-post__content">
        <header className="fm-post__head">
          <span className="fm-post__author">{post.authorName ?? "–"}</span>
          {post.isSystem && <span className="fm-badge">{t("forum.bot")}</span>}
          {post.isModerator && (
            <span className="fm-badge mod">
              <Icon name="shield" size={11} /> {t("forum.moderator")}
            </span>
          )}
          {post.isOriginalPoster && <span className="fm-badge op">{t("forum.op")}</span>}
          {post.authorBanned && <span className="fm-badge danger">{t("forum.mod.banned")}</span>}
          {!!post.openReports && (
            <button type="button" className="fm-badge report" onClick={showReports} title={t("forum.mod.showReports")}>
              <Icon name="flag" size={11} /> {post.openReports === 1 ? t("forum.mod.report1") : t("forum.mod.reports", { n: post.openReports })}
            </button>
          )}
          <span className="fm-post__time" title={fullDate(post.createdAt, locale)}>
            {timeAgo(post.createdAt, locale)}
          </span>
          {post.edited && (
            <button
              type="button"
              className={`fm-post__edited ${canModerate && post.revisions ? "is-clickable" : ""}`}
              title={post.editedAt ? `${t("forum.edited")} ${fullDate(post.editedAt, locale)}` : undefined}
              onClick={() => canModerate && post.revisions && onHistory(post)}
            >
              · {t("forum.edited")}
            </button>
          )}
        </header>

        {post.replyTo && (
          <a className="fm-replyto" href={`#p-${post.replyTo.id}`}>
            <Icon name="reply" size={13} /> {t("forum.replyingTo", { name: post.replyTo.authorName ?? "–" })}
          </a>
        )}

        {post.status === "deleted" && <p className="fm-post__gone">{t("forum.deletedPost")}</p>}
        {post.status === "hidden" && <p className="fm-post__gone">{t("forum.hiddenPost")}</p>}

        {editing ? (
          <div className="fm-post__edit">
            <Composer
              value={draft}
              onChange={setDraft}
              minRows={4}
              onSubmit={() => act(() => forum.editPost(post.id, draft)).then(() => setEditing(false))}
            />
            <div className="fm-actions">
              <button
                type="button"
                className="fm-btn fm-btn--primary fm-btn--sm"
                disabled={!draft.trim()}
                onClick={() => act(() => forum.editPost(post.id, draft)).then(() => setEditing(false))}
              >
                {t("forum.save")}
              </button>
              <button type="button" className="fm-btn fm-btn--ghost fm-btn--sm" onClick={() => setEditing(false)}>
                {t("forum.cancel")}
              </button>
              <span className="fm-muted fm-small">{t("forum.editNote")}</span>
            </div>
          </div>
        ) : (
          post.body && <Markdown text={post.body} className={index === 0 ? "is-lg" : ""} />
        )}

        {!editing && post.status !== "deleted" && (
          <footer className="fm-post__actions">
            <button
              type="button"
              className={`fm-vote ${vote.mine ? "is-on" : ""}`}
              onClick={toggleVote}
              disabled={!loggedIn || post.mine || post.isSystem || !ok}
              title={post.mine ? t("forum.ownPost") : t("forum.upvote")}
            >
              <Icon name="up" size={15} filled={vote.mine} />
              <span>{vote.score}</span>
            </button>
            {loggedIn && ok && (!locked || canModerate) && (
              <button type="button" className="fm-act" onClick={() => onReply(post)}>
                <Icon name="reply" size={15} /> {t("forum.reply")}
              </button>
            )}
            <button
              type="button"
              className="fm-act"
              onClick={() => {
                copyLink(`/forum/t/${window.location.pathname.split("/").pop()}#p-${post.id}`);
                setNote(t("forum.linkCopied"));
              }}
            >
              <Icon name="share" size={15} /> {t("forum.share")}
            </button>
            {loggedIn && (
              <Menu label={<Icon name="more" size={16} />} title={t("forum.more")}>
                {post.mine && ok && (
                  <>
                    <MenuItem
                      icon="edit"
                      onClick={() => {
                        setDraft(post.body ?? "");
                        setEditing(true);
                      }}
                    >
                      {t("forum.edit")}
                    </MenuItem>
                    <MenuItem
                      icon="trash"
                      danger
                      onClick={() => window.confirm(t("forum.deleteConfirm")) && act(() => forum.deletePost(post.id))}
                    >
                      {t("forum.delete")}
                    </MenuItem>
                  </>
                )}
                {!post.mine && !post.isSystem && ok && (
                  <MenuItem
                    icon="flag"
                    onClick={() => {
                      const reason = window.prompt(t("forum.reportPrompt"));
                      if (reason === null) return;
                      forum
                        .report(post.id, reason)
                        .then(() => setNote(t("forum.reported")))
                        .catch((e) => setNote(errorText(e)));
                    }}
                  >
                    {t("forum.report")}
                  </MenuItem>
                )}
                {canModerate && !post.isSystem && (
                  <MenuItem icon="eye" onClick={() => act(() => forum.modHidePost(post.id, post.status !== "hidden"))}>
                    {post.status === "hidden" ? t("forum.modShow") : t("forum.modHide")}
                  </MenuItem>
                )}
                {canModerate && !!post.revisions && (
                  <MenuItem icon="history" onClick={() => onHistory(post)}>
                    {t("forum.history", { n: post.revisions })}
                  </MenuItem>
                )}
                {canModerate && !!post.openReports && (
                  <>
                    <MenuItem icon="flag" onClick={showReports}>
                      {t("forum.mod.showReports")}
                    </MenuItem>
                    <MenuItem icon="check" onClick={() => act(() => forum.modResolveReports(post.id))}>
                      {t("forum.mod.dismissReports")}
                    </MenuItem>
                  </>
                )}
                {canModerate && post.authorId && !post.mine && (
                  <>
                    <div className="fm-menu-sep" />
                    <div className="fm-menu-label">{post.authorName}</div>
                    <MenuItem icon="edit" onClick={renameAuthor}>
                      {t("forum.mod.rename")}
                    </MenuItem>
                    <MenuItem icon="shield" danger={!post.authorBanned} onClick={toggleBan}>
                      {post.authorBanned ? t("forum.mod.unban") : t("forum.mod.ban")}
                    </MenuItem>
                  </>
                )}
              </Menu>
            )}
            {note && <span className="fm-note">{note}</span>}
          </footer>
        )}
      </div>
    </article>
  );
};

// /forum/t/:id
export const ForumThreadPage = () => {
  const { id } = useParams();
  const { t, locale } = useLanguage();
  const forum = useForumApi();
  const { me, openName, dialog } = useForumMe();
  const { categories, byslug, name } = useCategories();
  const [detail, setDetail] = useState<ForumThreadDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [body, setBody] = useState("");
  const [replyTo, setReplyTo] = useState<ForumPost | null>(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [highlight, setHighlight] = useState<string | null>(null);
  const [history, setHistory] = useState<{ post: ForumPost; revisions: { body: string; replacedAt: string }[] } | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const composer = useRef<ComposerHandle>(null);
  const composerBox = useRef<HTMLDivElement>(null);

  const load = useCallback(() => {
    if (!id) return Promise.resolve();
    return forum
      .thread(id)
      .then(setDetail)
      .catch((e) => setError(e instanceof ForumError && e.code === "NotFound" ? t("forum.notFound") : t("forum.loadError")));
  }, [forum, id, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (id) forum.view(id);
  }, [forum, id]);

  // Scroll to and flash the post in the URL hash (shared links, reply chips).
  useEffect(() => {
    const onHash = () => {
      const pid = window.location.hash.replace(/^#p-/, "");
      if (!pid) return;
      document.getElementById(`p-${pid}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlight(pid);
      window.setTimeout(() => setHighlight(null), 1800);
    };
    if (detail) onHash();
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, [detail]);

  const th = detail?.thread;
  const cat = byslug(th?.category);
  // The daily thread's opening post repeats its card as text; show the card.
  const posts = useMemo(
    () => (detail ? detail.posts.filter((p, i) => !(i === 0 && p.isSystem && th?.kind === "daily" && th.data)) : []),
    [detail, th],
  );
  const op = detail?.posts[0];

  const startReply = (post: ForumPost) => {
    if (!me?.forumName) {
      openName();
      return;
    }
    setReplyTo(post);
    track("feature_use", "forum_quote");
    const quote = stripMarkdown(post.body ?? "").slice(0, 220);
    if (quote && !(post.isSystem && th?.kind === "daily")) composer.current?.insert(`> ${quote}${quote.length >= 220 ? "…" : ""}\n\n`);
    else composer.current?.focus();
    composerBox.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const send = async () => {
    if (!th || !body.trim()) return;
    setSending(true);
    setSendError(null);
    try {
      const r = await forum.reply(th.id, body, replyTo?.id);
      track("feature_use", th.kind === "daily" ? "forum_reply_daily" : "forum_reply");
      setBody("");
      setReplyTo(null);
      await load();
      window.history.replaceState(null, "", `#p-${r.id}`);
      window.dispatchEvent(new HashChangeEvent("hashchange"));
    } catch (err) {
      if (err instanceof ForumError && err.code === "ForumNameRequired") openName();
      setSendError(errorText(err));
    } finally {
      setSending(false);
    }
  };

  const mod = async (patch: { isPinned?: boolean; isLocked?: boolean; isHidden?: boolean; title?: string; category?: string }) => {
    if (!th) return;
    try {
      await forum.modThread(th.id, patch);
      load();
    } catch (e) {
      setError(errorText(e));
    }
  };

  const showHistory = async (post: ForumPost) => {
    const r = await forum.modRevisions(post.id);
    setHistory({ post, revisions: r.revisions });
  };

  const loggedIn = !!me;
  const canModerate = !!detail?.canModerate;

  return (
    <Shell>
      <div className="fm-crumbs">
        <Link to="/forum">{t("forum.back")}</Link>
        {cat && (
          <>
            <span aria-hidden>›</span>
            <Link to={`/forum/c/${cat.slug}`}>{name(cat)}</Link>
          </>
        )}
      </div>
      {error && <div className="alert alert-warning">{error}</div>}
      {!detail && !error && <Loading />}
      {detail && th && (
        <div className="fm-layout fm-layout--thread">
          <main className="fm-main">
            <header className="fm-thread-head" style={{ "--cat": cat?.color ?? "#2563eb" } as React.CSSProperties}>
              <div className="fm-thread-head__chips">
                {cat && <CategoryChip name={name(cat)} color={cat.color} icon={cat.icon} />}
                {th.isPinned && (
                  <span className="fm-badge">
                    <Icon name="pin" size={11} /> {t("forum.pinned")}
                  </span>
                )}
                {th.isLocked && (
                  <span className="fm-badge muted">
                    <Icon name="lock" size={11} /> {t("forum.locked")}
                  </span>
                )}
                {th.isHidden && <span className="fm-badge danger">{t("forum.hiddenThread")}</span>}
              </div>
              <h1 className="fm-thread-head__title">{th.title}</h1>
              <div className="fm-thread-head__meta">
                <Avatar name={th.authorName} system={th.kind === "daily"} size={24} />
                <span className="fm-row__author">{th.authorName}</span>
                <span className="fm-dotsep" />
                <span title={fullDate(th.createdAt, locale)}>{timeAgo(th.createdAt, locale)}</span>
                <span className="fm-dotsep" />
                <span>
                  <Icon name="comment" size={13} /> {th.replyCount}
                </span>
                <span className="fm-dotsep" />
                <span title={t("forum.viewsLabel")}>
                  <Icon name="eye" size={13} /> {th.viewCount.toLocaleString(locale)}
                </span>
                <span className="fm-dotsep" />
                <span>
                  <Icon name="up" size={13} filled /> {op?.score ?? 0}
                </span>
                {canModerate && (
                  <Menu
                    label={
                      <>
                        <Icon name="shield" size={15} /> <span className="fm-iconbtn__label">{t("forum.mod")}</span>
                      </>
                    }
                    title={t("forum.mod")}
                  >
                    <MenuItem icon="pin" onClick={() => mod({ isPinned: !th.isPinned })}>
                      {th.isPinned ? t("forum.modUnpin") : t("forum.modPin")}
                    </MenuItem>
                    <MenuItem icon="lock" onClick={() => mod({ isLocked: !th.isLocked })}>
                      {th.isLocked ? t("forum.modUnlock") : t("forum.modLock")}
                    </MenuItem>
                    <MenuItem icon="edit" onClick={() => setRenaming(th.title)}>
                      {t("forum.modRename")}
                    </MenuItem>
                    <MenuItem icon="eye" danger onClick={() => mod({ isHidden: !th.isHidden })}>
                      {th.isHidden ? t("forum.modShow") : t("forum.modHideThread")}
                    </MenuItem>
                  </Menu>
                )}
              </div>
              {canModerate && categories && (
                <label className="fm-move">
                  {t("forum.modMove")}
                  <select value={th.category} onChange={(e) => mod({ category: e.target.value })}>
                    {categories.map((c) => (
                      <option key={c.slug} value={c.slug}>
                        {name(c)}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </header>

            {th.kind === "daily" && th.data && (
              <section className="fm-daily-wrap">
                <DailyCard data={th.data} />
              </section>
            )}

            <div className="fm-posts">
              {posts.map((p, i) => (
                <PostCard
                  key={p.id}
                  post={p}
                  index={th.kind === "daily" ? i + 1 : i}
                  canModerate={canModerate}
                  loggedIn={loggedIn}
                  locked={th.isLocked}
                  highlight={highlight === p.id}
                  onReply={startReply}
                  onChanged={load}
                  onHistory={showHistory}
                />
              ))}
            </div>

            <section className="fm-reply-box" ref={composerBox}>
              {!me ? null : me.banned ? (
                <p className="fm-muted">{t("forum.bannedNotice")}</p>
              ) : th.isLocked && !canModerate ? (
                <p className="fm-muted">
                  <Icon name="lock" size={14} /> {t("forum.lockedNotice")}
                </p>
              ) : !me.forumName ? (
                <div className="fm-reply-box__intro">
                  <p className="fm-muted">{t("forum.publicNotice")}</p>
                  <button type="button" className="fm-btn fm-btn--primary" onClick={openName}>
                    {t("forum.nameTitle")}
                  </button>
                </div>
              ) : (
                <>
                  <div className="fm-reply-box__head">
                    <Avatar name={me.forumName} size={32} />
                    <span>
                      {t("forum.replyAs")} <b>{me.forumName}</b>
                    </span>
                    {replyTo && (
                      <span className="fm-replyto is-static">
                        <Icon name="reply" size={13} /> {t("forum.replyingTo", { name: replyTo.authorName ?? "–" })}
                        <button type="button" onClick={() => setReplyTo(null)} aria-label={t("forum.cancel")}>
                          ×
                        </button>
                      </span>
                    )}
                  </div>
                  <Composer
                    ref={composer}
                    value={body}
                    onChange={setBody}
                    onSubmit={send}
                    placeholder={t("forum.replyPlaceholder")}
                  />
                  {sendError && <div className="fm-note danger">{sendError}</div>}
                  <div className="fm-reply-box__foot">
                    <span className="fm-muted fm-small">{t("forum.publicNotice")}</span>
                    <button type="button" className="fm-btn fm-btn--primary" disabled={sending || !body.trim()} onClick={send}>
                      {sending ? t("forum.sending") : t("forum.send")}
                    </button>
                  </div>
                </>
              )}
            </section>
          </main>

          <aside className="fm-aside">
            <section className="fm-panel">
              <div className="fm-panel__title">{t("forum.aboutThread")}</div>
              <dl className="fm-facts">
                <div>
                  <dt>{t("forum.started")}</dt>
                  <dd>{fullDate(th.createdAt, locale)}</dd>
                </div>
                <div>
                  <dt>{t("forum.lastActivity")}</dt>
                  <dd>{timeAgo(th.lastPostAt, locale)}</dd>
                </div>
                <div>
                  <dt>{t("forum.repliesLabel")}</dt>
                  <dd>{th.replyCount}</dd>
                </div>
                <div>
                  <dt>{t("forum.viewsLabel")}</dt>
                  <dd>{th.viewCount.toLocaleString(locale)}</dd>
                </div>
              </dl>
              <div className="fm-panel__sub">{t("forum.participants")}</div>
              <AvatarStack names={th.participants} size={30} />
              <button
                type="button"
                className="fm-btn fm-btn--ghost fm-btn--block fm-mt"
                onClick={() => copyLink(`/forum/t/${th.id}`)}
              >
                <Icon name="share" size={15} /> {t("forum.copyLink")}
              </button>
            </section>
          </aside>
        </div>
      )}

      <Modal show={!!history} onHide={() => setHistory(null)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{t("forum.historyTitle")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {history?.post.body && (
            <div className="fm-rev is-current">
              <div className="fm-rev__head">{t("forum.currentVersion")}</div>
              <Markdown text={history.post.body} />
            </div>
          )}
          {history?.revisions.map((r, i) => (
            <div className="fm-rev" key={i}>
              <div className="fm-rev__head">{t("forum.replacedAt", { date: fullDate(r.replacedAt, locale) })}</div>
              <Markdown text={r.body} />
            </div>
          ))}
        </Modal.Body>
      </Modal>

      <Modal show={renaming !== null} onHide={() => setRenaming(null)} centered>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (renaming) mod({ title: renaming }).then(() => setRenaming(null));
          }}
        >
          <Modal.Header closeButton>
            <Modal.Title>{t("forum.modRename")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <input
              className="fm-input"
              value={renaming ?? ""}
              onChange={(e) => setRenaming(e.target.value)}
              maxLength={160}
              autoFocus
            />
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="fm-btn fm-btn--ghost" onClick={() => setRenaming(null)}>
              {t("forum.cancel")}
            </button>
            <button type="submit" className="fm-btn fm-btn--primary" disabled={(renaming ?? "").trim().length < 5}>
              {t("forum.save")}
            </button>
          </Modal.Footer>
        </form>
      </Modal>
      {dialog}
    </Shell>
  );
};

// ---------------------------------------------------------------------------
// New thread
// ---------------------------------------------------------------------------

// /forum/ny?category=slug
export const NewThreadPage = () => {
  const { t } = useLanguage();
  const forum = useForumApi();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { me, openName, dialog } = useForumMe();
  const { categories, name, desc } = useCategories();
  const [category, setCategory] = useState(params.get("category") || "");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  const choices = useMemo(
    () => (categories ?? []).filter((c) => !c.systemOnly || me?.canModerate),
    [categories, me],
  );
  useEffect(() => {
    if (!choices.length) return;
    if (!choices.some((c) => c.slug === category)) {
      setCategory(choices.find((c) => c.slug === "allmant")?.slug ?? choices[0].slug);
    }
  }, [choices, category]);

  const submit = async () => {
    if (!me?.forumName) {
      openName();
      return;
    }
    setSending(true);
    setError(null);
    try {
      const r = await forum.createThread(category, title, body);
      track("feature_use", "forum_thread");
      navigate(`/forum/t/${r.id}`);
    } catch (err) {
      if (err instanceof ForumError && err.code === "ForumNameRequired") openName();
      setError(errorText(err));
    } finally {
      setSending(false);
    }
  };

  const ready = title.trim().length >= 5 && body.trim().length > 0 && !!category;

  return (
    <Shell>
      <div className="fm-crumbs">
        <Link to="/forum">{t("forum.back")}</Link>
      </div>
      <div className="fm-layout fm-layout--thread">
        <main className="fm-main">
          <header className="fm-new-head">
            <h1 className="fm-thread-head__title">{t("forum.newThread")}</h1>
            <p className="fm-muted">{t("forum.newLead")}</p>
          </header>
          {me?.banned ? (
            <p className="fm-muted">{t("forum.bannedNotice")}</p>
          ) : (
            <form
              className="fm-new"
              onSubmit={(e) => {
                e.preventDefault();
                submit();
              }}
            >
              <div className="fm-field">
                <span className="fm-field__label">{t("forum.category")}</span>
                <div className="fm-cat-pick">
                  {choices.map((c) => (
                    <button
                      key={c.slug}
                      type="button"
                      className={`fm-cat-pick__opt ${category === c.slug ? "is-active" : ""}`}
                      style={{ "--cat": c.color ?? "#64748b" } as React.CSSProperties}
                      onClick={() => setCategory(c.slug)}
                    >
                      <span className="fm-cat-tile__icon">
                        <Icon name={c.icon ?? "chat"} size={16} />
                      </span>
                      <span>
                        <span className="fm-cat-pick__name">{name(c)}</span>
                        <span className="fm-cat-pick__desc">{desc(c)}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>
              <label className="fm-field">
                <span className="fm-field__label">{t("forum.threadTitle")}</span>
                <input
                  className="fm-input fm-input--lg"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={160}
                  placeholder={t("forum.titlePlaceholder")}
                />
              </label>
              <div className="fm-field">
                <span className="fm-field__label">{t("forum.threadBody")}</span>
                <Composer value={body} onChange={setBody} minRows={9} onSubmit={() => ready && submit()} placeholder={t("forum.bodyPlaceholder")} />
              </div>
              {error && <div className="alert alert-danger">{error}</div>}
              <div className="fm-new__foot">
                <span className="fm-muted fm-small">
                  {me?.forumName ? (
                    <>
                      {t("forum.yourName", { name: me.forumName })}
                      {!me.nameLocked && (
                        <>
                          {" · "}
                          <button type="button" className="fm-linkbtn" onClick={openName}>
                            {t("forum.changeName")}
                          </button>
                        </>
                      )}
                    </>
                  ) : (
                    t("forum.publicNotice")
                  )}
                </span>
                <button type="submit" className="fm-btn fm-btn--primary" disabled={sending || (!!me?.forumName && !ready)}>
                  {me && !me.forumName ? t("forum.nameTitle") : sending ? t("forum.sending") : t("forum.create")}
                </button>
              </div>
            </form>
          )}
        </main>
        <aside className="fm-aside">
          <section className="fm-panel">
            <div className="fm-panel__title">
              <Icon name="spark" size={15} /> {t("forum.tipsTitle")}
            </div>
            <ul className="fm-rules">
              <li>{t("forum.tip1")}</li>
              <li>{t("forum.tip2")}</li>
              <li>{t("forum.tip3")}</li>
            </ul>
          </section>
          <section className="fm-panel">
            <div className="fm-panel__title">{t("forum.formatTitle")}</div>
            <ul className="fm-format-help">
              <li>
                <code>**fet**</code> → <b>fet</b>
              </li>
              <li>
                <code>*kursiv*</code> → <i>kursiv</i>
              </li>
              <li>
                <code>&gt; citat</code>
              </li>
              <li>
                <code>- punkt</code>
              </li>
              <li>
                <code>```kod```</code>
              </li>
            </ul>
          </section>
        </aside>
      </div>
      {dialog}
    </Shell>
  );
};

// The old category route renders the forum home filtered to that category.
export const ForumCategoryPage = ForumHome;
